<template>
  <div class="row">
    <div class="col-8 offset-2 text-center my-auto mb-md-5">
      <h1 class="my-auto">
        Vos factures
      </h1>
    </div>
    <div
      v-if="invoices && invoices.length > 0"
      class="col-2"
    >
      <div class="d-flex flex-column">
        <div
          class="d-flex ms-auto my-auto bg-blue-700 rounded-circle"
          style="width:42px; height: 42px"
        >
          <font-awesome-icon
            icon="filter"
            style="width:19px; height: 19px"
            class="cursor-pointer text-white m-auto"
            @click="openFilter"
          />
        </div>
        <div class="d-none d-md-flex mt-2 ms-auto fw-bold text-blue-700">
          Filtres
        </div>
      </div>
    </div>
    <v-filter
      v-if="isModalOpen"
      :filters="filters"
      @update:filter="updateBadgeInFilter"
      @apply-filter="applyFilterOnInvoices"
      @reset-filter="resetFilter"
      @close-filter="closeFilter"
    />
    <div
      v-for="(invoiceStatus, index) in invoicesContainer"
      :key="index"
      class="col-12"
    >
      <div class="h5 mt-2 mb-3">
        {{ statusToString[index] }}
      </div>
      <v-display
        v-for="(invoice, nature) in invoiceStatus"
        :key="`${invoice.invoiceId}-${nature}`"
        :item="invoice"
        class="mb-2"
      />
    </div>
    <v-no-resource v-if="invoices && invoices.length === 0">
      <div class="d-flex flex-column">
        <div class="lead">
          Aucune facture disponible.
        </div>
        <div class="lead">
          On dirait que vous
          n’avez pas encore souscrit de contrat chez nous !
        </div>
        <div class="mt-2">
          <a
            :href="getAppUrl('')"
            class="btn btn-primary fs-5 fw-bold"
          >
            Ajouter un nouveau contrat
          </a>
        </div>
      </div>
    </v-no-resource>
  </div>
</template>
<script>
import { uniq, mapValues, get } from 'lodash-es';
import { mapGetters } from 'vuex';

import Display from '@/components/invoices/helpers/InvoiceItemDisplay.vue';
import FiltersModal from '@/components/common/filters/FiltersModal.vue';

export default {
  name: 'InvoicesHome',
  components: {
    'v-display': Display,
    'v-filter': FiltersModal,
  },
  data() {
    return {
      filters: [],
      invoicesContainer: [],
      productsColor: {
        1: 'blue-500',
        2: 'pink-400',
        4: 'yellow-400',
        5: 'green-400',
        9: 'blue-300',
        13: 'pink-300',
        16: 'yellow-400',
        18: 'green-300',
        19: 'blue-300',
        29: 'yellow-300',
      },
      productsIsWhite: {
        1: true,
        2: true,
        4: false,
        5: false,
        9: false,
        13: false,
        16: false,
        18: false,
        19: false,
        29: false,
      },
      statusToString: [
        'En attente de paiement',
        'Factures en cours',
        'Historique de paiement',
        'Factures résiliées',
      ],
      sortingStatus: [
        ['OVERDUE'],
        ['IN_PROGRESS', 'CHECKED_OUT', 'PENDING'],
        ['PAID'],
        ['TERMINATED', 'CANCELLED'],
      ],
    };
  },
  computed: {
    ...mapGetters({
      invoices: 'invoices/items',
      products: 'rater/products',
      contracts: 'contracts/get',
      isModalOpen: 'ui/focusScreen',
    }),
    allProducts() {
      return uniq(this.invoices
        .map((invoice) => invoice.productId || get(invoice, 'product.id'))
        .filter(Boolean));
    },
    allReferences() {
      return uniq(this.invoices.map(({ subscriptionReference }) => subscriptionReference));
    },
    allYears() {
      const years = this.invoices.map(({ effectiveDate }) => effectiveDate)
        .map((date) => (new Date(date)).getFullYear())
        .sort((a, b) => a - b);
      return uniq(years);
    },
  },
  beforeUnmount() {
    this.$store.dispatch('ui/focusScreen', false);
  },
  mounted() {
    this.filters = [{
      title: 'Type de contrat',
      badges: this.allProducts.map(this.buildProductBadges),
    },
    {
      title: 'Références',
      badges: this.allReferences.map(this.buildReferenceBadges),
    },
    {
      title: 'Années',
      badges: this.allYears.map(this.buildYearsBadges),
    }];
    this.applyFilterOnInvoices();
  },
  methods: {
    getAppUrl() {
      return `${import.meta.env.VITE_WIDGET_URL}`;
    },
    // Switch view of filter
    openFilter() {
      this.$store.dispatch('ui/focusScreen', true);
    },
    closeFilter() {
      this.$store.dispatch('ui/focusScreen', false);
    },
    resetFilter() {
      this.filters = this.filters.map((elem) => Object.assign(elem, {
        badges: elem.badges.map((badge) => Object.assign(badge, { active: true })),
      }));
    },

    reduceInvoices(invoices) {
      const reduces = [...invoices]
        .filter(({ visible, parentId }) => visible && !parentId)
        .reduce((acc, invoice) => {
          const index = this.sortingStatus
            .findIndex((status) => status.includes(invoice.status));
          if (acc[index]) {
            acc[index].push(invoice);
          } else {
            acc[index] = [invoice];
          }
          return acc;
        }, {});
      return mapValues(reduces, (invoicesByStatus) => invoicesByStatus
        .sort(({ effectiveDate: a }, { effectiveDate: b }) => {
          const aFullYear = (new Date(a)).getTime();
          const bFullYear = (new Date(b)).getTime();
          return (bFullYear - aFullYear);
        }));
    },

    // builder for badges
    buildProductBadges(productId) {
      const { name } = (this.products.find(({ id: elemId }) => elemId === productId) || { name: '' });
      return {
        name,
        key: productId,
        active: true,
        color: this.productsColor[productId],
        isWhite: this.productsIsWhite[productId],
      };
    },
    buildYearsBadges(year) {
      // eslint-disable-next-line object-curly-newline
      return { name: year, key: year, color: 'yellow-300', active: true };
    },
    buildReferenceBadges(reference) {
      // eslint-disable-next-line object-curly-newline
      return { name: reference, key: reference, color: 'white', active: true };
    },

    // Set badge active
    updateBadgeInFilter({ filterIndex, index }) {
      const isActive = this.filters[filterIndex].badges[index].active;
      this.filters[filterIndex].badges[index].active = !isActive;
    },

    filterByType(invoice) {
      const productId = invoice.productId || get(invoice, 'product.id');
      const actives = this.filters[0].badges
        .filter(({ active }) => active)
        .map(({ key }) => key);
      if (actives.length === 0) return true;
      return actives.includes(productId);
    },
    filterByReference({ subscriptionReference }) {
      const actives = this.filters[1].badges
        .filter(({ active }) => active)
        .map(({ key }) => key);
      if (actives.length === 0) return true;
      return actives.includes(subscriptionReference);
    },
    filterByYears({ effectiveDate }) {
      const actives = this.filters[2].badges
        .filter(({ active }) => active)
        .map(({ key }) => key);
      if (actives.length === 0) return true;
      const year = (new Date(effectiveDate)).getFullYear();
      return actives.includes(year);
    },

    applyFilterOnInvoices() {
      const invoices = this.invoices
        .filter(this.filterByReference)
        .filter(this.filterByType)
        .filter(this.filterByYears);
      this.invoicesContainer = this.reduceInvoices(invoices);
      this.closeFilter();
    },
  },
};
</script>
